import React from 'react';

/**
 * Button — the single button used across the platform.
 * Flat design (no gradients / shadows), consistent sizes and radii.
 *
 *   <Button variant="primary" size="md" leftIcon={<Plus/>} loading={saving}>Save</Button>
 *   <Button variant="danger-soft" icon={<Trash2/>} aria-label="Delete" />   // icon-only
 *
 * variant: primary | secondary | outline | ghost | soft | success | danger | danger-soft
 * size:    sm | md | lg
 * props:   leftIcon, rightIcon, icon (icon-only), loading, disabled, block, ...button attrs
 */

const VARIANTS = {
  primary: 'bg-blue-600 text-white border-transparent hover:bg-blue-700',
  secondary: 'bg-white text-[#334155] border-[#E3E9F3] hover:bg-[#F5F8FC] dark:bg-[#262b33] dark:text-gray-200 dark:border-gray-700 dark:hover:bg-[#31373f]',
  outline: 'bg-transparent text-blue-600 border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20',
  ghost: 'bg-transparent text-[#64748B] border-transparent hover:bg-[#F5F8FC] hover:text-[#0B1220] dark:text-gray-400 dark:hover:bg-[#31373f] dark:hover:text-gray-100',
  soft: 'bg-[#EEF3FF] text-blue-600 border-transparent hover:bg-[#E1EAFF] dark:bg-blue-900/20 dark:text-blue-300',
  success: 'bg-emerald-600 text-white border-transparent hover:bg-emerald-700',
  danger: 'bg-rose-600 text-white border-transparent hover:bg-rose-700',
  'danger-soft': 'bg-[#FFF1F3] text-rose-600 border-transparent hover:bg-[#FFE0E7] dark:bg-rose-900/20 dark:text-rose-400',
};

const SIZES = {
  sm: 'h-[34px] px-3 text-[13px] gap-1.5 rounded-[10px]',
  md: 'h-10 px-4 text-[13.5px] gap-2 rounded-xl',
  lg: 'h-[46px] px-5 text-[14.5px] gap-2 rounded-xl',
};

const ICON_SIZES = {
  sm: 'w-[34px] h-[34px] rounded-[10px]',
  md: 'w-10 h-10 rounded-xl',
  lg: 'w-[46px] h-[46px] rounded-xl',
};

const Spinner = () => (
  <span className="inline-block w-[15px] h-[15px] border-2 border-current border-r-transparent rounded-full animate-spin" />
);

const Button = React.forwardRef(({
  variant = 'primary',
  size = 'md',
  type = 'button',
  leftIcon,
  rightIcon,
  icon,
  loading = false,
  disabled = false,
  block = false,
  className = '',
  children,
  ...rest
}, ref) => {
  const iconOnly = !!icon && (children === undefined || children === null || children === false);
  const base = 'inline-flex items-center justify-center font-semibold border cursor-pointer transition-colors whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:opacity-50 disabled:pointer-events-none';
  const sizeCls = iconOnly ? ICON_SIZES[size] || ICON_SIZES.md : SIZES[size] || SIZES.md;
  const variantCls = VARIANTS[variant] || VARIANTS.primary;

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={`${base} ${sizeCls} ${variantCls} ${block ? 'w-full' : ''} ${className}`}
      {...rest}
    >
      {iconOnly ? (
        loading ? <Spinner /> : icon
      ) : (
        <>
          {loading ? <Spinner /> : leftIcon}
          {children}
          {!loading && rightIcon}
        </>
      )}
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
